import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";

import { Col, Row, Typography } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import { cn } from "lib/utils";
import { toast } from "utils/toast";

import { useGetAdvisorCredits } from "hooks/useAccounts";
import { useJoinOrganization } from "hooks/useJoinOrganization";

import CreditCardSection from "js/components/CreditCardSection";
import CreateReportModal from "js/components/Dashboard/CreateReportModal";
import DashboardLoading from "js/components/Dashboard/DashboardLoading";
import QuotaOnboardingTutorial, {
  shouldShowQuotaTutorial,
} from "js/components/QuotaOnboardingTutorial";
import InvestigationFilters from "js/components/Dashboard/InvestigationFilters";
import TenantReportFilters from "js/components/Dashboard/TenantReportFilters";
import InvestigationCardList from "js/components/Investigations/InvestigationCardList";
import InvestigationTable from "js/components/Investigations/InvestigationTable";
import TableSearch from "js/components/TableSearch";
import TenantReportsWithQuery from "js/components/Tenants/TenantReportsWithQuery";
import { Button } from "js/components/ui/button";
import Tabs, { TabsProps } from "js/components/ui/tabs";

import RentalApplicationModal from "js/pages/Tenants/CreateRentalApplication";

import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { loadOrganizationData } from "../../../store/features/organization/organizationActions";

const { Title, Text } = Typography;

type InvestigationFiltersType = {
  is_active?: string;
  status?: "created" | "in_progress" | "completed" | "invalid";
  legal_type?: "natural" | "legal";
};

type TenantFiltersType = {
  is_active?: string;
  status?: string;
  legal_type?: string;
};

const HEADER_ROW_STYLE: React.CSSProperties = {
  marginBottom: 15,
  marginTop: 24,
};

/** Row with search, optional filters, and a primary button */
function ControlsRow(props: {
  isLg: boolean;
  searchLabel: string;
  onSearch: (v: string) => void;
  onClear: () => void;
  filters?: React.ReactNode;
  rightButton: React.ReactNode;
  className?: string;
}) {
  const {
    isLg,
    searchLabel,
    onSearch,
    onClear,
    filters,
    rightButton,
    className,
  } = props;
  return (
    <div
      className={cn(
        "flex flex-col gap-3",
        isLg ? "md:flex-row md:items-center" : "",
        className,
      )}
    >
      <div className="w-full min-w-[240px] md:max-w-md">
        <TableSearch
          screen={{ lg: isLg } as any}
          onSearch={onSearch}
          onClear={onClear}
          label={searchLabel}
        />
      </div>
      {filters && (
        <div className="flex w-full flex-1 flex-wrap gap-2 md:justify-start">
          {filters}
        </div>
      )}
      <div className="flex w-full justify-end md:w-auto md:pl-4">
        {rightButton}
      </div>
    </div>
  );
}

/** Renders table on medium+ screens and card list on smaller screens */
function ResponsiveList<T extends object>(props: {
  isMd: boolean;
  Table: React.ComponentType<any>;
  Cards: React.ComponentType<any>;
  commonProps: Record<string, any>;
}) {
  const { isMd, Table, Cards, commonProps } = props;
  return (
    <Row gutter={[16, 16]}>
      <Col xs={24}>
        {isMd ? <Table {...commonProps} /> : <Cards {...commonProps} />}
      </Col>
    </Row>
  );
}

const Dashboard = () => {
  const dispatch = useAppDispatch();
  const { userData } = useAppSelector((state) => state.entities.user);
  const {
    organizationData,
    loading: isOrganizationLoading,
    error: organizationError,
  } = useAppSelector((state) => state.entities.organization);
  const {
    data: advisorCreditsResponse,
    isLoading: isCreditsLoading,
    isFetching: isCreditsFetching,
    refetch: refetchCredits,
  } = useGetAdvisorCredits();
  const advisorCreditsData = advisorCreditsResponse?.quotes;

  const [investigationSearch, setInvestigationSearch] = useState("");
  const [tenantSearch, setTenantSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showRentalApplicationModal, setShowRentalApplicationModal] =
    useState(false);
  const [tenantFilters, setTenantFilters] = useState<TenantFiltersType>({});
  const [investigationFilters, setInvestigationFilters] =
    useState<InvestigationFiltersType>({});
  const [showQuotaTutorial, setShowQuotaTutorial] = useState(false);

  const screen = useBreakpoint();
  const navigate = useNavigate();
  const skeletonTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slowStateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const { error, success, joinMessage } = useJoinOrganization();

  const hasCreditsResolved = advisorCreditsResponse !== undefined;
  const isCreditsBootstrapping =
    isCreditsLoading || (isCreditsFetching && !hasCreditsResolved);
  const isBootLoading = isOrganizationLoading || isCreditsBootstrapping;

  const [showSkeleton, setShowSkeleton] = useState<boolean>(
    () => isBootLoading,
  );
  const [showSlowState, setShowSlowState] = useState(false);

  useEffect(() => {
    if (isBootLoading) {
      if (skeletonTimeoutRef.current) {
        clearTimeout(skeletonTimeoutRef.current);
        skeletonTimeoutRef.current = null;
      }
      setShowSkeleton(true);
    } else {
      skeletonTimeoutRef.current = setTimeout(() => {
        setShowSkeleton(false);
        skeletonTimeoutRef.current = null;
      }, 450);
    }

    return () => {
      if (skeletonTimeoutRef.current) {
        clearTimeout(skeletonTimeoutRef.current);
        skeletonTimeoutRef.current = null;
      }
    };
  }, [isBootLoading]);

  useEffect(() => {
    if (!isBootLoading || !showSkeleton) {
      if (slowStateTimeoutRef.current) {
        clearTimeout(slowStateTimeoutRef.current);
        slowStateTimeoutRef.current = null;
      }
      setShowSlowState(false);
    } else if (!slowStateTimeoutRef.current) {
      slowStateTimeoutRef.current = setTimeout(() => {
        setShowSlowState(true);
      }, 8000);
    }

    return () => {
      if (slowStateTimeoutRef.current) {
        clearTimeout(slowStateTimeoutRef.current);
        slowStateTimeoutRef.current = null;
      }
    };
  }, [isBootLoading, showSkeleton]);

  const organizationErrorMessage = useMemo(() => {
    if (!organizationError) return null;
    if (typeof organizationError === "string") return organizationError;
    if (organizationError instanceof Error) return organizationError.message;
    return null;
  }, [organizationError]);

  const hasCreditHistModule = useMemo(
    () =>
      (organizationData as any)?.modules?.includes("credit_history") ?? false,
    [organizationData],
  );
  const hasTenantModule = useMemo(
    () => (organizationData as any)?.modules?.includes("tenant") ?? false,
    [organizationData],
  );

  const organizationId = (userData as any)?.organization_id ?? null;

  // Show quota onboarding tutorial once loading finishes (only if never seen)
  useEffect(() => {
    if (
      !isBootLoading &&
      organizationData &&
      hasCreditHistModule &&
      shouldShowQuotaTutorial()
    ) {
      setShowQuotaTutorial(true);
    }
  }, [isBootLoading, organizationData, hasCreditHistModule]);

  const handleRetry = useCallback(() => {
    if (organizationId) {
      dispatch(loadOrganizationData(organizationId));
    }
    refetchCredits();
  }, [dispatch, organizationId, refetchCredits]);

  useEffect(() => {
    let reloadTimer: ReturnType<typeof setTimeout> | undefined;

    if (error) {
      toast.error(joinMessage || "Link de invitación invalido/expirado.");
    } else if (success) {
      const loadingToast = toast.loading("Loading...");
      setTimeout(() => {
        toast.dismiss(loadingToast);
        toast.success("Link de invitación aceptado.", { duration: 1000 });
      }, 1000);
      reloadTimer = setTimeout(() => {
        window.location.href = window.location.pathname;
      }, 2500);
    }

    return () => {
      if (reloadTimer) clearTimeout(reloadTimer);
    };
  }, [error, success, joinMessage]);

  const handleInvestigationSearch = useCallback(
    (v: string) => setInvestigationSearch(v),
    [],
  );
  const clearInvestigationSearch = useCallback(
    () => setInvestigationSearch(""),
    [],
  );
  const handleTenantSearch = useCallback((v: string) => setTenantSearch(v), []);
  const clearTenantSearch = useCallback(() => setTenantSearch(""), []);

  const dashboardTitle = useMemo(() => {
    if (hasCreditHistModule && hasTenantModule) return "Reportes";
    if (hasCreditHistModule) return "Historial Crediticio";
    if (hasTenantModule) return "Solicitudes de Renta";
    return "";
  }, [hasCreditHistModule, hasTenantModule]);

  const renderCreditHistorySection = () => (
    <div style={HEADER_ROW_STYLE}>
      <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white/90">
        <ControlsRow
          isLg={!!screen.lg}
          searchLabel="reporte(s)"
          onSearch={handleInvestigationSearch}
          onClear={clearInvestigationSearch}
          className="gap-4 p-4 pb-3"
          rightButton={
            <Button
              onClick={() => setShowModal(true)}
              size="lg"
              className="w-full text-white sm:w-auto bg-[#0060ff] hover:bg-[#0052d4]"
            >
              Crear Reporte
            </Button>
          }
          filters={
            <InvestigationFilters
              onFilterChange={setInvestigationFilters}
              filters={investigationFilters}
            />
          }
        />
        <div className="border-t border-slate-200/70 bg-white/95 px-2 pb-4 pt-3">
          <ResponsiveList
            isMd={!!screen.md}
            Table={InvestigationTable}
            Cards={InvestigationCardList}
            commonProps={{
              showAdvisorColumn: false,
              searchFilter: investigationSearch,
              additionalFilters: investigationFilters,
              isOrganization: false,
            }}
          />
        </div>
      </div>
    </div>
  );

  const renderTenantSection = () => (
    <div style={HEADER_ROW_STYLE}>
      <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white/90">
        <ControlsRow
          isLg={!!screen.lg}
          searchLabel="solicitud(es)"
          onSearch={handleTenantSearch}
          onClear={clearTenantSearch}
          className="gap-4 p-4 pb-3"
          rightButton={
            <Button
              onClick={() => setShowRentalApplicationModal(true)}
              size="lg"
              className="w-full text-white md:max-w-[250px] bg-[#0060ff] hover:bg-[#0052d4]"
            >
              Crear Solicitud de Renta
            </Button>
          }
          filters={
            <TenantReportFilters
              onFilterChange={setTenantFilters}
              filters={tenantFilters}
            />
          }
        />
        <div className="border-t border-slate-200/70 bg-white/95 px-2 pb-4 pt-3">
          <TenantReportsWithQuery
            isMd={!!screen.md}
            searchFilter={tenantSearch}
            additionalFilters={tenantFilters}
          />
        </div>
      </div>
    </div>
  );

  const tabItems: TabsProps["items"] = useMemo(() => {
    if (!(hasCreditHistModule && hasTenantModule)) return [];
    return [
      {
        key: "1",
        label: "Reportes de Hist. Credit.",
        children: renderCreditHistorySection(),
      },
      {
        key: "2",
        label: "Solicitudes de Renta",
        children: renderTenantSection(),
      },
    ];
  }, [
    hasCreditHistModule,
    hasTenantModule,
    investigationSearch,
    investigationFilters,
    tenantSearch,
    tenantFilters,
    screen,
  ]);

  return (
    <div id="home">
      {showSkeleton ? (
        <DashboardLoading
          showSlowState={showSlowState}
          onRetry={showSlowState ? handleRetry : undefined}
          className="mt-6"
        />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            <Col
              xs={24}
              sm={12}
              md={24}
              lg={6}
              style={{
                display: "flex",
                alignItems: "start",
                justifyContent: screen.xs && !screen.sm ? "flex-start" : "left",
              }}
            >
              <Title level={1} style={{ marginBottom: 5 }}>
                {dashboardTitle}
              </Title>
            </Col>
            <Col xs={24} lg={18}>
              {advisorCreditsData && organizationData && (
                <CreditCardSection
                  credits={advisorCreditsData}
                  position="end"
                />
              )}
            </Col>
          </Row>

          {!organizationData && !isOrganizationLoading && (
            <Row className="mt-6">
              <Col xs={24} md={18} lg={14}>
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white/95 p-6 shadow-xs">
                  <Title level={4} style={{ marginBottom: 12 }}>
                    Aún no perteneces a una organización
                  </Title>
                  <Text className="mb-4 block text-slate-600">
                    {organizationErrorMessage
                      ? `No pudimos cargar la organización: ${organizationErrorMessage}.`
                      : "Para consultar reportes necesitas unirte a una organización existente o crear una nueva."}
                  </Text>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      asChild
                      size="lg"
                      className="bg-[#0060ff] text-white hover:bg-[#0052d4]"
                    >
                      <Link to="/organizations/create">
                        Crear una organización
                      </Link>
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleRetry}
                      disabled={!organizationId}
                      className="border-slate-200 text-slate-700 hover:bg-slate-100"
                    >
                      Reintentar carga
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {hasCreditHistModule && hasTenantModule ? (
            <Tabs
              className="mt-5"
              defaultActiveKey="1"
              items={tabItems}
              destroyOnHidden
            />
          ) : hasCreditHistModule ? (
            renderCreditHistorySection()
          ) : hasTenantModule ? (
            renderTenantSection()
          ) : null}

          <CreateReportModal
            showModal={showModal}
            credits={advisorCreditsData}
            setShowModal={setShowModal}
          />
          <RentalApplicationModal
            isOpen={showRentalApplicationModal}
            onOpenChange={setShowRentalApplicationModal}
          />
        </>
      )}

      <QuotaOnboardingTutorial
        open={showQuotaTutorial}
        onOpenChange={setShowQuotaTutorial}
      />
    </div>
  );
};

export default Dashboard;
